import { inject } from '@angular/core';
import { RedirectFunction, Router, Routes, Route } from '@angular/router';
import { LandingComponent } from './landing/landing.component';
import { welcomeGateGuard } from './core/guards/welcome-gate.guard';
import { moduleEnabledGuard } from './core/guards/module-enabled.guard';
import { platformAdminGuard } from './core/auth/platform-admin.guard';

/**
 * Cut K4 (0.72.36, BDR-034/BDR-037) — Redirect in einen Worklist-Tab.
 *
 * /modules/tasks und /modules/inbox sind seit diesem Cut Redirects auf die
 * Tabs, die sie abgeloest haben. Ein statischer redirectTo-String kann keinen
 * Query-Parameter tragen, deshalb die Funktions-Form: eine RedirectFunction
 * laeuft im Injection-Context und darf einen UrlTree zurueckgeben.
 *
 * Der zurueckgegebene UrlTree ERSETZT den ganzen Baum: /modules/tasks?x=1#y
 * landet auf /modules/worklist?tab=tasks und verliert x und y. Die
 * String-Redirects daneben (modules/dev-dashboard, modules/releases) behalten
 * beides, weil ein relativer redirectTo-String in den bestehenden Baum
 * hineingerechnet wird. Heute geht dabei nichts verloren: keine der beiden
 * verschobenen Komponenten liest einen Query-Parameter oder ein Fragment, und
 * kein Link im Baum schickt eines an einen der beiden Pfade.
 *
 * needsGuard() am Dateiende ueberspringt jede Route mit redirectTo, und eine
 * Funktion ist truthy — die beiden Redirects bekommen also KEINEN
 * welcomeGateGuard, genau wie die String-Redirects daneben. Das ist so
 * gewollt: Angular verbietet redirectTo + canActivate (NG04014), und der
 * Guard sitzt auf dem Ziel /modules/worklist.
 *
 * Gepinnt von modules/worklist/worklist-route-redirects.spec.ts.
 *
 * WER HIER EINE WEITERE REDIRECT-FUNKTION ANLEGT, MUSS DAS WISSEN:
 * app.routes.spec.ts RUFT jede RedirectFunction dieser Tabelle auf, um ihr
 * Ziel einzusammeln — in einem TestBed, dessen einziger einschlaegiger
 * Provider provideRouter([]) ist. inject(Router) geht deshalb; injiziert eine
 * Redirect-Funktion einen Applikations-Service, faellt dieser Spec mit
 * NullInjectorError um, in einer Datei, die man gerade nicht bearbeitet.
 * Dann entweder den Provider dort ergaenzen oder ohne Injection auskommen.
 */
const toWorklistTab = (tab: 'tasks' | 'approvals'): RedirectFunction =>
  () => inject(Router).parseUrl(`/modules/worklist?tab=${tab}`);

const RAW_APP_ROUTES: Routes = [
  // 0.58.1 — Welcome-Gate als root-Page. Wer auf admin.fatip.ch landet,
  // wählt zuerst zwischen Voucher-Einlösen oder FED-Login. Authentifizierte
  // User werden im AppComponent direkt nach /modules redirected (siehe
  // app.component.ts initial-redirect-Logik).
  { path: '', pathMatch: 'full', redirectTo: 'welcome' },
  {
    path: 'welcome',
    loadComponent: () =>
      import('./modules/welcome-gate/welcome-gate.component').then(m => m.WelcomeGateComponent),
    title: 'FATIP — Willkommen'
  },
  // 0.45.3: FATIP rebrand across all tab titles. Internal code
  // identifiers (CSS prefix `.fap-*`, event types, API paths,
  // SpiceDB object ids) remain on FAP — cosmetic rename only.
  // 0.58.1 / 0.61.8 — welcomeGateGuard wird via Map am Ende der
  // Datei AUF ALLE modules/*-Sub-Routes angewendet, nicht mehr nur hier
  // auf der Root-Route. Vorher konnten anonyme User direkt
  // /modules/applications etc. aufrufen. Seit Cut P3a reitet der Guard
  // nur noch auf modules/*: der Developer-Subtree traegt seinen Guard
  // in developer.routes.ts (auf dem Landing-Child), nicht über diese Map.
  {
    path: 'modules',
    component: LandingComponent,
    title: 'FATIP — Alle Module',
  },
  // A3 T3 — Anonymous public-catalog explore landing.
  // Top-level route; needsGuard() returns false (not a modules/* path)
  // → no welcomeGateGuard applied. /explore/apis is T4.
  {
    path: 'explore',
    loadComponent: () =>
      import('./modules/explore/explore-landing.component').then(m => m.ExploreLandingComponent),
    title: 'FATIP — Entdecken'
  },
  // A3 T4 — Anonymous read-only public API catalog.
  // Top-level route; needsGuard() returns false → no welcomeGateGuard applied.
  {
    path: 'explore/apis',
    loadComponent: () =>
      import('./modules/explore/explore-apis.component').then(m => m.ExploreApisComponent),
    title: 'FATIP — API-Katalog'
  },
  // 0.57.60 ADR-040 — Business-Case + Vision-Page. Erklärt Stakeholdern
  // (BIT, Bundeskanzlei, externe Reviewer) warum FATIP gebraucht wird.
  // Self-contained; soll auch via direktem Link erreichbar sein.
  // 0.62.93 (BUG-008 #13 #2) — renamed /why → /spotlight (platform-
  // configurable content, no longer a fixed "Why FATIP" page). Old
  // /why links (bookmarks, external references) keep working via the
  // redirectTo route right below — must stay anonymous too (needsGuard()
  // skips redirectTo routes, see comment below).
  {
    path: 'spotlight',
    loadComponent: () =>
      import('./modules/why/why-fatip.component').then(m => m.WhyFatipComponent),
    title: 'FATIP — Spotlight'
  },
  { path: 'why', redirectTo: 'spotlight', pathMatch: 'full' },
  // 0.57.98 ADR-058 Phase 7-Pilot — Break-Glass-Login-UI. Standalone-
  // Route, kein Auth-Guard. Backend-Endpoint POST /v1/auth/break-glass
  // existiert seit längerem; hier ist nur der UI-Wrapper.
  {
    path: 'break-glass',
    loadComponent: () =>
      import('./modules/break-glass/break-glass-login.component').then(m => m.BreakGlassLoginComponent),
    title: 'FATIP — Notfall-Login'
  },
  // 0.62.x ADR-088 — Anonyme Voucher-Redeem-Page für eingeladene Personen.
  // Standalone-Route, kein Auth-Guard. Backend-Endpoints sind
  // GET /v1/vouchers/{code} (validate) + POST /v1/vouchers/redeem (redeem).
  {
    path: 'voucher',
    loadComponent: () =>
      import('./modules/voucher/voucher-redeem-page.component').then(m => m.VoucherRedeemPageComponent),
    title: 'FATIP — Voucher einlösen'
  },
  // Cut P6d2 (ADR-169 part 6) — die Bestätigungsseite der Rezert-Decision-
  // Links. Standalone-Route, kein Auth-Guard: der Token im Pfad ist das
  // Authentifizierungsmaterial, dieselbe token-anonyme Surface wie die
  // Endpunkte P6d1s (GET /v1/recert/links/{token} + POST
  // /v1/recert/links/confirm). needsGuard() schliesst sie automatisch aus
  // (kein modules/*-Pfad) — kein Ausschluss nötig. Die entfernte
  // Self-Cert-Route `recert/:token` (Cut K3c, Kommentar unten) bleibt
  // entfernt: dieser Pfad ist ein anderer.
  {
    path: 'recert/decision/:token',
    loadComponent: () =>
      import('./modules/recert-decision/recert-decision.component').then(m => m.RecertDecisionComponent),
    title: 'FATIP — Rezertifizierung'
  },
  // 0.61.28 — Anonyme Invite-Page (Phase 1 Minimal-Stub).
  // Zeigt Voucher-Status + Begin-Registration-Button. Der Click
  // konsumiert die state_nonce + redirected zur IdP-Registration.
  // Der OIDC-Callback applied dann die Pending-Grants (0.61.28.1).
  {
    path: 'invite/:code',
    loadComponent: () =>
      import('./modules/invite/invite-page.component').then(m => m.InvitePageComponent),
    title: 'FATIP — Einladung'
  },
  // Pfad C.4 (ADR-072 / ADR-073) — /login Switchboard.
  // Zentraler Login-Eingang mit drei Pfaden:
  //   - IdP-OIDC-SSO (Standard)
  //   - Bootstrap-Owner (Initial-Setup)
  //   - Notfall-Login / Break-Glass
  // Header-Anmelden-Button routet hierher (statt direkt /bootstrap).
  {
    path: 'login',
    loadComponent: () =>
      import('./modules/login/login-switchboard.component').then(m => m.LoginSwitchboardComponent),
    title: 'FATIP — Anmeldung'
  },
  {
    path: 'login/callback',
    loadComponent: () =>
      import('./modules/login/login-callback.component').then(m => m.LoginCallbackComponent),
    title: 'FATIP — Anmeldung läuft'
  },
  // ADR-058 Phase 4 — Bootstrap-Owner-Pfad. Versteckter URL-Pfad fuer
  // den initialen Platform-Admin-Login nach frischem Deployment.
  // Discovery-Schutz: kein Link in der Navigation, kein Redirect von
  // anderen Routes. URL muss bekannt sein (Operations-Doc).
  // Backend-Endpoint POST /v1/auth/break-glass (gleicher wie /break-glass);
  // bootstrap-Owner haben totp_required=FALSE → TOTP optional im Form.
  // On force_password_change=true → /bootstrap/change-password.
  {
    path: 'bootstrap',
    loadComponent: () =>
      import('./modules/bootstrap/bootstrap-login.component').then(m => m.BootstrapLoginComponent),
    title: 'FATIP — Bootstrap'
  },
  {
    path: 'bootstrap/change-password',
    loadComponent: () =>
      import('./modules/bootstrap/bootstrap-change-password.component').then(m => m.BootstrapChangePasswordComponent),
    title: 'FATIP — Bootstrap (Passwort ändern)'
  },
  // 0.57.42 — Onboarding für Invite-via-Email + AGOV. callback-Route
  // zuerst damit der path-segment 'agov-callback' nicht als :token
  // interpretiert wird.
  // Cut P4a (BDR-045): die bare onboarding-Route (kein Token) rendert die
  // Übersicht über die drei Onboarding-Pfade; anonym wie die zwei
  // Geschwister (needsGuard() wrappt nur modules/*).
  {
    path: 'onboarding',
    loadComponent: () =>
      import('./modules/onboarding/onboarding-page.component').then(m => m.OnboardingPageComponent),
    title: 'FATIP — Onboarding'
  },
  {
    path: 'onboarding/agov-callback',
    loadComponent: () =>
      import('./modules/onboarding/onboarding-page.component').then(m => m.OnboardingPageComponent),
    title: 'FATIP — Onboarding'
  },
  {
    path: 'onboarding/:token',
    loadComponent: () =>
      import('./modules/onboarding/onboarding-page.component').then(m => m.OnboardingPageComponent),
    title: 'FATIP — Onboarding'
  },
  // Cut K3c (0.72.26, BDR-034): die Route `recert/:token` und
  // `modules/self-recert/` sind entfernt. Sie waren die oeffentliche
  // Self-Cert-Seite der Gruppen-Rezertifizierung (ADR-048, jetzt superseded)
  // und die einzige anonyme Route der Rezertifizierungs-Oberflaeche; der Endpunkt
  // `GET`/`POST /v1/recert/self/{token}` dahinter ist mit demselben Cut aus
  // dem Router verschwunden. Kein Redirect: die Seite war nur ueber einen
  // Token-Link aus einer Mail erreichbar, die niemand mehr bekommt.
  // Rezertifizierung Generation D lebt weiter: als Tab im Org-Baum (Owner-
  // Review) und seit Cut P6d2 als Route `recert/decision/:token` in genau
  // dieser Tabelle — die Bestätigungsseite der Decision-Links (Kommentar
  // oben beim Voucher-Pfad). Der entfernte Pfad `recert/:token` bleibt
  // reserviert; die neue Route ist ein anderer Pfad.
  // Gepinnt von `app.routes.spec.ts`.
  // ADR-146 (WS2a, CA/PKI-Descope): CA-Module-Routen + Legacy-Redirects
  // entfernt (zertifikate-mgmt, pki-ops, pki-ops/certificates, certificates,
  // ca-hierarchy, issuance-workflow, cert-issuance). PKI ist geparkt
  // (BDR-028 E4); die Module + ihre ca-Client-Services sind weg.
  {
    path: 'modules/my-account',
    loadChildren: () =>
      import('./modules/my-account/my-account.routes').then(m => m.MY_ACCOUNT_ROUTES)
  },
  {
    // BUG-008 #21 — dev-dashboard Modul entfernt. Redirect → /modules damit
    // Bookmarks + Browser-History nicht in 404 enden.
    // Cut K4 (BDR-034/BDR-037) ist das dort angekuendigte Follow-Up: Komponente,
    // core/api/dev-dashboard.service.ts, beide nginx-Locations, GET
    // /v1/dev-dashboard/me und sein Handler sind weg. Nur dieser Redirect bleibt.
    path: 'modules/dev-dashboard',
    redirectTo: 'modules',
    pathMatch: 'full',
  },
  {
    // 0.55.2: Inbox — vereinte Approval-Requests-Projection (siehe ADR-008).
    // 0.57.70 W-22: primärer Einstieg ist /modules/worklist?tab=approvals.
    // Cut K4: die Seite ist keine eigene Route mehr, die Komponente ist der
    // Genehmigungen-Tab der Worklist. Der Pfad bleibt als Redirect dorthin,
    // damit Direkt-Links und Audit-Trails nicht in 404 enden.
    path: 'modules/inbox',
    redirectTo: toWorklistTab('approvals'),
    pathMatch: 'full',
  },
  // 0.57.70 W-22 — Unified Worklist mit Tabs Aufgaben + Genehmigungen.
  // Brief-Icon im Header linkt jetzt hierher (siehe task-inbox.component.ts).
  {
    path: 'modules/worklist',
    loadComponent: () =>
      import('./modules/worklist/worklist.component').then(m => m.WorklistComponent),
    title: 'FATIP — Worklist'
  },
  {
    path: 'modules/organisations',
    loadChildren: () =>
      import('./modules/organisations/organisations.routes').then(m => m.ORG_ROUTES)
  },
  {
    path: 'modules/docs',
    loadChildren: () =>
      import('./modules/docs/docs.routes').then(m => m.DOCS_ROUTES)
  },
  {
    // 2026-05-30 Audit K7/S28 — system-tools exponiert K8s/DB/OpenBao-Root-Token-
    // Runbooks. Backend-Modulkatalog steuert nur die Tile-Sichtbarkeit, NICHT die
    // direkte Route-Navigation → platformAdminGuard erzwingen (Muster wie
    // bulk-onboarding.routes.ts:15).
    path: 'modules/system-tools',
    canMatch: [platformAdminGuard],
    loadComponent: () =>
      import('./modules/system-tools/system-tools.component').then(m => m.SystemToolsComponent),
    title: 'FATIP — System-Tools'
  },
  {
    // Merged into Roadmap since 0.31.1 — Release-Notes sind jetzt Teil
    // der "Veröffentlicht"-Spalte mit Click-to-Changelog. Alte Deep-
    // Links auf /modules/releases landen automatisch dort.
    path: 'modules/releases',
    redirectTo: 'modules/roadmap',
    pathMatch: 'full'
  },
  {
    path: 'modules/roadmap',
    loadComponent: () =>
      import('./modules/roadmap/roadmap.component').then(m => m.RoadmapComponent),
    title: 'FATIP — Roadmap & Releases'
  },
  // BDR-028 (WS3, Mockup-Removal): audit-center-Mockup-Modul entfernt
  // (Mock-JSON, nicht der echte /modules/audit-Tab). ca-hierarchy +
  // issuance-workflow waren bereits mit ADR-146 (WS2a) entfallen.
  {
    path: 'modules/identities',
    loadComponent: () =>
      import('./modules/identities/identities.component').then(m => m.IdentitiesComponent),
    title: 'FATIP — Identitäten'
  },
  {
    // Must be declared before the :id route so "data-corrections"
    // isn't swallowed as an identity id.
    path: 'modules/identities/data-corrections',
    loadComponent: () =>
      import('./modules/identities/data-corrections-inbox.component').then(m => m.DataCorrectionsInboxComponent),
    title: 'FATIP — Daten-Korrekturen'
  },
  {
    // Must be declared before the :id route so "corrections" isn't swallowed as an identity id.
    path: 'modules/identities/corrections',
    pathMatch: 'full',
    redirectTo: 'modules/identities/data-corrections',
  },
  {
    // 0.56.2 — Person-Detail-View mit Tabs (ADR-009 Variante A).
    // Lookup über global_id (NanoID), nicht UUID. Vor :id-Route
    // damit "persons" nicht als Identity-ID interpretiert wird.
    path: 'modules/identities/persons/:global_id',
    loadComponent: () =>
      import('./modules/identities/person-detail.component').then(m => m.PersonDetailComponent),
    title: 'FATIP — Identitäts-Detail'
  },
  {
    path: 'modules/identities/:id',
    loadComponent: () =>
      import('./modules/identities/identity-detail.component').then(m => m.IdentityDetailComponent),
    title: 'FATIP — Identität'
  },
  {
    // 0.57.8 — Bulk-Onboarding hinter platformAdminGuard (ADR-015).
    path: 'modules/bulk-onboarding',
    loadChildren: () =>
      import('./modules/bulk-onboarding/bulk-onboarding.routes').then(m => m.BULK_ONBOARDING_ROUTES)
  },
  {
    path: 'modules/applications',
    loadChildren: () =>
      import('./modules/applications/apps.routes').then(m => m.APPS_ROUTES)
  },
  {
    // API-Management Cut 1, Task 6 — Gateway-Registry. Guard-Muster wie
    // modules/system-tools + modules/cloud-governance: Backend-Modulkatalog
    // steuert nur die Tile-Sichtbarkeit, NICHT die direkte Route-Navigation
    // → platformAdminGuard erzwingen (spiegelt gateways.go's
    // callerIsPlatformAdmin-Gate auf /v1/gateways). welcomeGateGuard kommt
    // zusätzlich über die needsGuard()-Map am Dateiende (jede modules/*-
    // Route bekommt ihn automatisch, kein separater Eintrag nötig).
    path: 'modules/api-management',
    canMatch: [platformAdminGuard],
    loadChildren: () =>
      import('./modules/api-management/api-management.routes').then(m => m.API_MANAGEMENT_ROUTES)
  },
  {
    // Cut P5e1 (Issue #781) — Agent-Registry. Guard-Muster wie
    // modules/api-management + modules/platform: der Backend-Modulkatalog
    // steuert nur die Tile-Sichtbarkeit, NICHT die direkte Route-Navigation
    // → platformAdminGuard erzwingen (spiegelt listTenantAgents'
    // callerIsPlatformAdmin-Gate auf /v1/identities/agents).
    // welcomeGateGuard kommt zusätzlich über die needsGuard()-Map am
    // Dateiende (jede modules/*-Route bekommt ihn automatisch).
    path: 'modules/agents',
    canMatch: [platformAdminGuard],
    loadChildren: () =>
      import('./modules/agents/agents.routes').then(m => m.AGENTS_ROUTES),
    title: 'FATIP — Agent Registry'
  },
  {
    // Cut K4: wie modules/inbox oben. Die Aufgaben-Seite ist der Aufgaben-Tab
    // der Worklist; der Pfad bleibt als Redirect dorthin. Der tab-Parameter
    // ist heute redundant (die Worklist startet ohne ?tab= auf Aufgaben) und
    // wird trotzdem geschrieben: das Brief-Icon im Header verlinkt dieselbe
    // URL mit demselben Parameter, ein kopierter Link behält seinen Tab, und
    // das Ziel hängt nicht am Default der Komponente.
    path: 'modules/tasks',
    redirectTo: toWorklistTab('tasks'),
    pathMatch: 'full',
  },
  {
    path: 'modules/audit',
    loadChildren: () =>
      import('./modules/audit/audit.routes').then(m => m.AUDIT_ROUTES)
  },
  {
    path: 'modules/help',
    loadChildren: () =>
      import('./modules/help/help.routes').then(m => m.HELP_ROUTES)
  },
  {
    // U37 (issue #410): der `enabled`-Flag des Backend-Modulkatalogs gate't
    // die Route (moduleEnabledGuard), während das Backend selbst 404
    // antwortet — das Re-Hosting ist T90s.
    path: 'modules/notifications',
    canMatch: [moduleEnabledGuard('notifications')],
    loadChildren: () =>
      import('./modules/notifications/notifications.routes').then(m => m.NOTIFICATIONS_ROUTES)
  },
  {
    path: 'modules/developer-reviews',
    loadComponent: () =>
      import('./modules/developer-reviews/developer-reviews.component').then(m => m.DeveloperReviewsComponent),
    title: 'FATIP — Entwickler-Registrierungen'
  },
  {
    path: 'modules/platform',
    canMatch: [platformAdminGuard],
    loadChildren: () =>
      import('./modules/platform/platform.routes').then(m => m.PLATFORM_ROUTES),
    title: 'FATIP — Plattform Management'
  },
  // BDR-028 (WS3, Mockup-Removal): EMBAG-Transparenz-Seiten tech-stack +
  // datamodel entfernt (E6, statisch). EMBAG-Art.-9-Pflicht ist anderweitig
  // zu erfüllen (Caveat im Design-Spec dokumentiert).
  {
    // UX-1 (Mandate-Graph) Task 3: Nachfolger von 'modules/mandate-graph'
    // (FE-only-Erklaerseite mit Mock-Daten, entfernt). Echte Sicht auf
    // Access-Graph-Daten der eigenen APIs ("Meine APIs").
    path: 'modules/mandate',
    loadComponent: () =>
      import('./modules/mandate/mandate.component').then(m => m.MandateComponent),
    title: 'FATIP: Mandatsgraph'
  },
  {
    // UX-1 (Mandate-Graph) Task 3: 'mandate-graph'-Modul-Eintrag ist aus
    // dem Catalog entfernt (Nachfolger: 'modules/mandate' oben). Redirect
    // bleibt registriert damit Bookmarks/Browser-History nicht in 404
    // enden (Muster: modules/dev-dashboard).
    path: 'modules/mandate-graph',
    redirectTo: 'modules/mandate',
    pathMatch: 'full',
  },
  {
    // 0.57.115 — Integration Guidelines für Konsumenten-App-Owner.
    // How-To-Page mit OIDC-Pattern, Token-Claims, Group-Memberships,
    // Environment-Scope, Code-Beispiele. Anonymous-readable.
    path: 'modules/integration-guidelines',
    loadComponent: () =>
      import('./modules/integration-guidelines/integration-guidelines.component').then(m => m.IntegrationGuidelinesComponent),
    title: 'FATIP — Integration Guidelines'
  },
  {
    path: 'modules/apis',
    loadChildren: () =>
      import('./modules/apis/apis.routes').then(m => m.APIS_ROUTES)
  },
  {
    // 0.57.34: Org-Mandate-Übersicht (ADR-024 Phase A, read-only).
    path: 'modules/mandates',
    loadComponent: () =>
      import('./modules/mandates/mandates-page.component').then(m => m.MandatesPageComponent),
    title: 'FATIP — Org-Mandate'
  },
  {
    // 0.62.x ADR-087 — PAM-Tile. Sichtbar nur wenn Plattform-Admin
    // die Org explizit enabled hat (default disabled).
    path: 'modules/pam',
    loadComponent: () =>
      import('./modules/pam/pam.component').then(m => m.PamComponent),
    title: 'FATIP — Privileged Access'
  },
  {
    // PAM Cut 3 T3 (ADR-137/ADR-139) — Cloud-Governance: Platform-
    // Admin-Control-Plane für JIT-Cloud-Access (Cloud-Accounts, Approver,
    // JIT-Targets auf /v1/platform/cloud-accounts*). Guard-Muster wie
    // modules/system-tools: platformAdminGuard ist ein CanMatchFn →
    // canMatch (nicht canActivate); welcomeGateGuard kommt zusätzlich via
    // APP_ROUTES-Map am Dateiende.
    path: 'modules/cloud-governance',
    canMatch: [platformAdminGuard],
    loadComponent: () =>
      import('./modules/cloud-governance/cloud-governance.component').then(m => m.CloudGovernanceComponent),
    title: 'FATIP — Cloud Governance'
  },
  // 0.58.5 — Cloud-Management-Route entfernt (PO-Entscheidung). Cloud-
  // Workspace-Übersicht/Kosten macht die Cloud-Plattform eigenständig auf ihrer
  // Plattform; ein Mock-Tile in FATIP würde die Source-of-Truth-Frage
  // unklar machen. Fokus auf PAM (genuin FATIP: Identity x JIT-Cloud-
  // Permission), siehe Header-Kommentar in services/ca/internal/api/modules.go.
  // 0.62.1.19e: /idp-Route entfernt. Das Lazy-Modul fuehrte den Hydra-
  // basierten Login/Consent/Logout-Flow — Hydra ist im Pilot nie produktiv
  // gegangen, der konfigurierte IdP (Keycloak seit dem Zitadel-Decommission
  // 2026-06-22) uebernimmt OIDC komplett. Backend-Routes /v1/idp/*
  // und das hydra-Subpaket wurden im selben Cut entfernt.
  {
    path: 'developer',
    loadChildren: () => import('./developer/developer.routes').then(m => m.DEVELOPER_ROUTES)
  },
  { path: '**', redirectTo: 'modules' }
];

/**
 * 0.61.8 — Welcome-Gate-Guard auf ALLE modules/*-Sub-Routes anwenden.
 *
 * PO-Bug 2026-05-03: Anonyme User konnten /modules/applications direkt
 * im URL eingeben + sogar eine App anlegen. Der ursprüngliche Guard
 * von 0.58.2 war NUR auf Parent-Route 'modules' (root) gesetzt — die
 * 30+ Sub-Routes waren ungeschützt.
 *
 * Pragmatische Lösung: Map über alle RAW_APP_ROUTES und prepend den
 * welcomeGateGuard zu jeder modules/*-Route. /idp ist mit
 * 0.62.1.19e entfallen. Cut P3a: das Developer-Sub-Modul wird nicht
 * mehr über diese Map geguarded — sein Landing-Child in
 * developer.routes.ts trägt den welcomeGateGuard selbst, damit ein
 * anonymer Besucher /developer/signup und /developer/verify erreicht,
 * während die Landing-Seite das Gate behält.
 *
 * Ausgenommen: '/welcome' (Gate selbst), '/voucher', '/login',
 * '/break-glass', '/spotlight' (+ '/why' als redirectTo-Alias, siehe
 * 0.62.93 BUG-008 #13 #2 oben) — diese müssen anonym bleiben (Auth-Flows
 * + öffentliche Stakeholder-Pages). '/why' braucht ohnehin keinen
 * expliziten Ausschluss mehr: redirectTo-Routes werden von needsGuard()
 * generell uebersprungen (siehe Kommentar direkt unten).
 */
function needsGuard(route: Route): boolean {
  if (!route.path) return false;
  // 0.61.10 — redirectTo-Routes ausschließen. Angular verbietet
  // `redirectTo` + `canActivate` zusammen (NG04014: redirects happen
  // before guards). Die Redirect-Targets selbst werden ja ohnehin
  // gewrappt, der Redirect-Hop braucht keinen Guard.
  // Betroffen: z.B. modules/releases → modules/roadmap, modules/mandate-graph
  // → modules/mandate. (Die CA-Redirects sind mit ADR-146 entfallen.)
  if (route.redirectTo) return false;
  return route.path === 'modules'
    || route.path.startsWith('modules/');
}

export const APP_ROUTES: Routes = RAW_APP_ROUTES.map(route => {
  if (!needsGuard(route)) return route;
  const existing = route.canActivate || [];
  // Prepend damit der Guard ZUERST läuft (idempotent: wenn schon drin, kein Doppelt-Eintrag).
  if (existing.includes(welcomeGateGuard)) return route;
  return { ...route, canActivate: [welcomeGateGuard, ...existing] };
});
